import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import './App.css';
import '@/index.css';

// Theme Provider
import { ThemeProvider } from './contexts/ThemeContext';

// Cycle Phase Provider — single source of truth for the canonical
// economic cycle phase consumed across Dashboard, Portfolio Detail,
// Rebalancing, Evaluator, Market Analysis and Advanced Cycle Analysis.
import { CyclePhaseProvider } from './context/CyclePhaseContext';

// Analytics
import { trackPageView, trackLogin, trackSignUp, identifyUser, setUserProperties, trackWaitlistSignup, trackCTAClicked } from './utils/analytics';

// Icons
import { TrendingUp, Shield, LineChart, Users, BookOpen, Building2, Menu, X, LogOut, BarChart3, PieChart as PieChartIcon, Wallet, Settings as SettingsIcon, Mail, CheckCircle, ArrowRight, Activity, Target, Zap, Clock, Home, AlertCircle } from 'lucide-react';

// Recharts for pie chart
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

// Components
import Navigation from './components/Navigation';
import ErrorBoundary from './components/ErrorBoundary';

// Pages - Lazy loaded for code splitting
const MarketAnalysis = React.lazy(() => import('./pages/MarketAnalysis'));
const AdvancedCycleAnalysis = React.lazy(() => import('./pages/AdvancedCycleAnalysis'));
const PortfolioBuilder = React.lazy(() => import('./pages/PortfolioBuilder'));
const PortfolioList = React.lazy(() => import('./pages/PortfolioList'));
const PortfolioDetail = React.lazy(() => import('./pages/PortfolioDetail'));
const PortfolioComparison = React.lazy(() => import('./pages/PortfolioComparison'));
const PortfolioRebalancing = React.lazy(() => import('./pages/PortfolioRebalancing'));
const PortfolioHealthPage = React.lazy(() => import('./pages/PortfolioHealthPage'));
const DeletePortfolio = React.lazy(() => import('./pages/DeletePortfolio'));
const EditPortfolio = React.lazy(() => import('./pages/EditPortfolio'));
const InstrumentComparison = React.lazy(() => import('./pages/InstrumentComparison'));
const InstrumentEvaluator = React.lazy(() => import('./pages/InstrumentEvaluator'));
const AIPortfolioBuilder = React.lazy(() => import('./pages/AIPortfolioBuilder'));
const Education = React.lazy(() => import('./pages/Education'));
const EducationAdvanced = React.lazy(() => import('./pages/EducationAdvanced'));
const EducationRebalancing = React.lazy(() => import('./pages/EducationRebalancing'));
const Brokers = React.lazy(() => import('./pages/Brokers'));
const Pricing = React.lazy(() => import('./pages/Pricing'));
const PaymentSuccess = React.lazy(() => import('./pages/PaymentSuccess'));
const Settings = React.lazy(() => import('./pages/Settings'));
const ActionGuidance = React.lazy(() => import('./pages/ActionGuidance'));
const Backtesting = React.lazy(() => import('./pages/Backtesting'));
const BacktestComparator = React.lazy(() => import('./pages/BacktestComparator'));
const AlertsCenter = React.lazy(() => import('./pages/AlertsCenter'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const TermsOfService = React.lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const ActivityPage = React.lazy(() => import('./pages/Activity'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const Contact = React.lazy(() => import('./pages/Contact'));
const LearnPage = React.lazy(() => import('./pages/LearnPage'));
import tutorialModules from './data/tutorialModules';
import CookieConsent from './components/CookieConsent';
import AuthForm from './components/AuthForm';
import NetworkBackdrop from './components/NetworkBackdrop';
// API Configuration
import { API, saveSessionToken, getSessionToken, clearSessionToken } from './config/api';

// Formatters
import { stripOptionScore, formatNumber } from './utils/formatters';
import { getRiskBand, getRiskBandColor, getRiskBandDescription, getRiskPercent, getRiskBandPosition, getRiskTooltip, RISK_MAX_SCORE } from './utils/riskProfile';
import { getFirstName } from './utils/displayName';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ==================== AUTH CALLBACK ====================
function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      try {
        const hash = location.hash;
        const sessionId = new URLSearchParams(hash.substring(1)).get('session_id');

        if (!sessionId) {
          navigate('/login');
          return;
        }

        const response = await axios.post(`${API}/auth/session`, {}, {
          headers: { 'X-Session-ID': sessionId },
          withCredentials: true
        });

        // Track login in Google Analytics
        const userData = response.data;
        trackLogin('google');
        identifyUser(userData.user_id);
        setUserProperties({
          subscription_type: userData.subscription || 'free',
          risk_profile: userData.risk_profile || 'unknown',
        });

        navigate('/dashboard', { state: { user: response.data } });
      } catch (error) {
        console.error('Auth error:', error);
        toast.error('Authentication failed');
        navigate('/login');
      }
    };

    processSession();
  }, [location, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-forest-900 dark:border-forest-400 mx-auto"></div>
        <p className="mt-4 text-gray-600 dark:text-gray-400">Authenticating...</p>
      </div>
    </div>
  );
}

// ==================== AUTH CONTEXT ====================
export const AuthContext = React.createContext(null);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Helper to make authenticated requests
  const authRequest = async (method, url, data = null) => {
    const token = getSessionToken();
    const config = {
      withCredentials: true,
      headers: token ? { 'X-Session-Token': token } : {}
    };
    
    if (method === 'get') {
      return axios.get(url, config);
    } else if (method === 'post') {
      return axios.post(url, data, config);
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await authRequest('get', `${API}/auth/me`);
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        setUser(null);
        setIsAuthenticated(false);
        clearSessionToken();
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = (userData) => {
    // Save session token if provided
    if (userData.session_token) {
      saveSessionToken(userData.session_token);
    }
    setUser(userData);
    setIsAuthenticated(true);
  };

  const logout = async () => {
    try {
      await authRequest('post', `${API}/auth/logout`, {});
    } catch (error) {
      // Ignore logout errors
    }
    clearSessionToken();
    setUser(null);
    setIsAuthenticated(false);
  };

  const refreshUser = async () => {
    try {
      const response = await authRequest('get', `${API}/auth/me`);
      setUser(response.data);
      setIsAuthenticated(true);
      return response.data;
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
      clearSessionToken();
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, isAuthenticated, login, logout, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// ==================== PROTECTED ROUTE ====================
function ProtectedRoute({ children }) {
  const { user, isLoading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-forest-900 dark:border-forest-400"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Authenticated layout: subtle network backdrop behind every protected route.
  return (
    <>
      <NetworkBackdrop opacity={0.05} color="#1a2b5b" animate={true} />
      <div style={{ position: 'relative', zIndex: 1 }}>
        {React.cloneElement(children, { user })}
      </div>
    </>
  );
}

// ==================== LANDING PAGE ====================
// ---- BRAND TOKENS (extracted from the official logo).
// Used inline across the landing for navy/blue accents that match the logo.
// Tailwind 'forest-*' tokens stay for the existing app theme; these are
// additive and surface only where the logo identity benefits visually.
const BRAND = {
  navy:        '#1a2b5b', // logo "Portfolio" text — top hierarchy / primary
  navyDeep:    '#0f1d44', // hover/pressed for navy
  blue:        '#00aaff', // logo "Engine" text — intelligence/data accent
  blueDeep:    '#0077ff', // logo gear body — pressed/strong accent
  blueGlow:    'rgba(0,170,255,0.18)',
  mint:        '#33cc99', // logo candlesticks — growth/positive
  mintDeep:    '#009973', // candlestick shadow — heavier mint
};

// ---- Combined product showcase (3D tilt + screenshot switching).
// Single source of truth for the "See it in action" section. Uses real
// screenshots from /tutorial-screenshots/. Each entry has a fallback name
// so missing files degrade to a colored gradient placeholder, never a
// broken-image icon.
const PRODUCT_SCREENSHOTS = [
  {
    id: 'dashboard',
    src: '/tutorial-screenshots/hero-dashboard.png',
    alt: 'Dashboard with economic cycle phase, health score and indicators',
    title: 'Live Dashboard',
    desc: 'Your complete portfolio overview with live cycle phase, health scores, and next actions — all in one view.',
    testId: 'preview-dashboard-img',
    accent: '#1D9E75',
  },
  {
    id: 'cycle',
    src: '/tutorial-screenshots/cycle-analysis.png',
    alt: 'Advanced cycle analysis with LEI, CEI and recession probability',
    title: 'Economic Cycle Analysis',
    desc: '17 FRED indicators · LEI / CEI / LAG composites · recession probability.',
    testId: 'preview-cycle-img',
    accent: '#378ADD',
  },
  {
    id: 'portfolio',
    src: '/tutorial-screenshots/portfolio-builder.png',
    alt: 'Portfolio builder with instrument search and cost basis input',
    title: 'Portfolio Builder',
    desc: 'Add stocks, ETFs, crypto, bonds. Enter your real cost basis. See actual P&L.',
    testId: 'preview-portfolio-img',
    accent: '#1D9E75',
  },
  {
    id: 'health',
    src: '/tutorial-screenshots/health-score.png',
    alt: 'Portfolio Health Score with 5 dimensions and insights',
    title: 'Portfolio Health Score',
    desc: '5 dimensions scored 0–100. Diversification, alignment, concentration, drift.',
    testId: 'preview-health-img',
    accent: '#1D9E75',
  },
  {
    id: 'evaluator',
    src: '/tutorial-screenshots/evaluator.png',
    alt: 'Instrument evaluator with company profile and cycle recommendation',
    title: 'Instrument Evaluator',
    desc: 'Any stock, ETF, bond or crypto — evaluated against the current cycle phase.',
    testId: 'preview-evaluator-img',
    accent: '#BA7517',
  },
  {
    id: 'rebalancing',
    src: '/tutorial-screenshots/rebalancing.png',
    alt: 'Rebalancing page showing 3 strategies and recommendations',
    title: 'Intelligent Rebalancing',
    desc: '3 strategies · cycle-aware targets · one-click apply.',
    testId: 'preview-rebalancing-img',
    accent: '#E24B4A',
  },
  {
    id: 'ai_builder',
    src: '/tutorial-screenshots/ai-builder.png',
    alt: 'AI portfolio builder with a generated proposal',
    title: 'AI Portfolio Builder',
    desc: 'Describe your goals. Get a cycle-aware allocation in seconds.',
    testId: 'preview-ai-img',
    accent: '#8E44AD',
  },
];

function MockupShowcase() {
  const [activeId, setActiveId] = useState(PRODUCT_SCREENSHOTS[0].id);
  const tiltRef = useRef(null);
  const rafRef = useRef(null);

  // Touch devices: disable tilt + perspective. Detect once on mount so SSR/prerender stays safe.
  const [isTouch, setIsTouch] = useState(false);
  useEffect(() => {
    const touch =
      typeof window !== 'undefined' &&
      ('ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0);
    setIsTouch(touch);
    if (tiltRef.current && !touch) {
      tiltRef.current.style.transform = 'rotateX(4deg) rotateY(-6deg)';
    }
  }, []);

  const handleMove = (e) => {
    if (isTouch || !tiltRef.current) return;
    const el = tiltRef.current;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      el.style.transition = 'transform 0.1s ease-out';
      el.style.transform = `rotateX(${(-y * 12).toFixed(2)}deg) rotateY(${(x * 16).toFixed(2)}deg)`;
    });
  };

  const handleLeave = () => {
    if (isTouch || !tiltRef.current) return;
    tiltRef.current.style.transition = 'transform 0.6s ease-out';
    tiltRef.current.style.transform = 'rotateX(4deg) rotateY(-6deg)';
  };

  return (
    <div className="grid md:grid-cols-5 gap-8 md:gap-10 items-center">
      {/* Feature picker — switches the active screenshot on hover/focus */}
      <div className="md:col-span-2 space-y-4" data-testid="mockup-feature-list">
        {PRODUCT_SCREENSHOTS.map((s) => {
          const active = s.id === activeId;
          return (
            <button
              key={s.id}
              type="button"
              onMouseEnter={() => setActiveId(s.id)}
              onFocus={() => setActiveId(s.id)}
              onClick={() => setActiveId(s.id)}
              data-testid={`mockup-feature-${s.id}`}
              aria-pressed={active}
              className={`w-full text-left p-5 rounded-xl border transition-all ${
                active
                  ? 'border-forest-500 bg-forest-50 shadow-md'
                  : 'border-gray-200 bg-white hover:border-forest-300 hover:shadow-sm'
              }`}
            >
              <h3 className="text-lg font-bold text-forest-900">{s.title}</h3>
              <p className="text-sm text-gray-600 mt-1 leading-relaxed">{s.desc}</p>
            </button>
          );
        })}
      </div>

      {/* 3D mockup */}
      <div
        className="md:col-span-3"
        style={{ perspective: isTouch ? 'none' : '1200px' }}
      >
        <div
          ref={tiltRef}
          onMouseMove={handleMove}
          onMouseLeave={handleLeave}
          data-testid="mockup-tilt-wrapper"
          className="relative mx-auto bg-white overflow-hidden"
          style={{
            transformStyle: isTouch ? 'flat' : 'preserve-3d',
            transition: 'transform 0.1s ease-out',
            maxWidth: '680px',
            borderRadius: '12px',
            border: '1px solid rgba(29, 158, 117, 0.2)',
            boxShadow:
              '0 32px 80px rgba(29, 158, 117, 0.15), 0 8px 32px rgba(0,0,0,0.12)',
          }}
        >
          {PRODUCT_SCREENSHOTS.map((s, i) => {
            const active = s.id === activeId;
            return (
              <ShowcaseImage
                key={s.id}
                screenshot={s}
                positionFirst={i === 0}
                visible={active}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ShowcaseImage({ screenshot, positionFirst, visible }) {
  const [errored, setErrored] = useState(false);
  const baseStyle = {
    position: positionFirst ? 'relative' : 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    opacity: visible ? 1 : 0,
    transition: 'opacity 0.3s ease-out',
    pointerEvents: 'none',
  };
  if (errored) {
    return (
      <div
        data-testid={screenshot.testId}
        aria-label={screenshot.alt}
        className="block w-full"
        style={{
          ...baseStyle,
          aspectRatio: '16 / 10',
          background: `linear-gradient(135deg, ${screenshot.accent} 0%, ${screenshot.accent}10 100%)`,
          color: 'white',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'inherit',
        }}
      >
        <span style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.01em' }}>
          {screenshot.title}
        </span>
        <span style={{ fontSize: 12, opacity: 0.8, marginTop: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          PortfolioEngine preview
        </span>
      </div>
    );
  }
  return (
    <img
      src={screenshot.src}
      alt={screenshot.alt}
      loading="lazy"
      data-testid={screenshot.testId}
      onError={() => setErrored(true)}
      className="block w-full h-auto"
      style={baseStyle}
    />
  );
}

// ---- Hero: animated headline + live economic cycle badge ----
const HERO_ANIM_CSS = `
/* --- Landing-wide network backdrop (sits behind every section) --- */
.landing-mesh-grain {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
}
.landing-mesh-grain::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 60% 30% at 30% 8%,  rgba(29,158,117,0.07) 0%, transparent 60%),
    radial-gradient(ellipse 50% 25% at 80% 18%, rgba(55,138,221,0.05) 0%, transparent 55%),
    radial-gradient(ellipse 40% 25% at 12% 45%, rgba(29,158,117,0.04) 0%, transparent 50%),
    radial-gradient(ellipse 50% 30% at 90% 60%, rgba(186,117,23,0.04) 0%, transparent 55%),
    radial-gradient(ellipse 60% 28% at 25% 80%, rgba(29,158,117,0.05) 0%, transparent 60%),
    radial-gradient(ellipse 45% 22% at 75% 92%, rgba(55,138,221,0.04) 0%, transparent 55%);
}
@media (prefers-reduced-motion: no-preference) {
  .landing-mesh-grain::before {
    animation: backdropDrift 24s ease-in-out infinite alternate;
  }
}
@keyframes backdropDrift {
  0%   { transform: translate3d(0, 0, 0); }
  100% { transform: translate3d(1.5%, -0.5%, 0); }
}
.landing-mesh-grain::after {
  content: '';
  position: absolute;
  inset: 0;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='180' height='180'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.55 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
  opacity: 0.045;
  mix-blend-mode: multiply;
}

/* --- Hero specific (kept for layered local effects) --- */
.hero-section { position: relative; }
.hero-section > * { position: relative; z-index: 1; }

@keyframes heroCharReveal {
  from { opacity: 0; transform: translateY(20px); filter: blur(4px); }
  to   { opacity: 1; transform: translateY(0);    filter: blur(0);   }
}
@keyframes heroSubIn {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}
.hero-headline-wrap {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  column-gap: 0.28em;
  row-gap: 0.18em; /* extra row breathing room so descenders never collide with the next line */
  line-height: 1.1;
}
/* Word mask: clips the slide-in animation but leaves descenders (g, p, y, j) visible.
   The padding-bottom + matching negative margin extends the visible area downward
   by 0.25em without affecting flow layout. */
.hero-word-mask {
  display: inline-block;
  overflow: hidden;
  vertical-align: bottom;
  padding: 0 0.04em 0.25em 0.04em;
  margin: 0 -0.04em -0.25em -0.04em;
}
.hero-word-inner {
  display: inline-flex;
}
.hero-char {
  display: inline-block;
  opacity: 0;
  will-change: transform, opacity, filter;
}
@media (prefers-reduced-motion: no-preference) {
  .hero-char {
    animation: heroCharReveal 0.4s cubic-bezier(0.16,1,0.3,1) both;
  }
}
.hero-sub-fade {
  opacity: 0;
}
@media (prefers-reduced-motion: no-preference) {
  .hero-sub-fade {
    animation: heroSubIn 0.6s cubic-bezier(0.25,0.46,0.45,0.94) forwards;
  }
}
@media (prefers-reduced-motion: reduce) {
  .hero-char,
  .hero-sub-fade {
    opacity: 1 !important;
    transform: none !important;
    filter: none !important;
  }
}

/* --- Section headings: bigger to read as section titles --- */
.section-title {
  font-size: clamp(2.25rem, 4vw, 3.25rem); /* 36px → 52px */
  line-height: 1.1;
  letter-spacing: -0.02em;
  font-weight: 700;
}
`;

const CYCLE_DOT_COLORS = {
  expansion: '#33cc99',
  recovery: '#378ADD',
  peak: '#BA7517',
  boom: '#BA7517',
  contraction: '#E24B4A',
  recession: '#E24B4A',
};

const CYCLE_LABEL = (phase) => {
  if (!phase) return null;
  const p = String(phase).toLowerCase();
  if (p.startsWith('exp')) return 'Expansion';
  if (p.includes('recovery')) return 'Recovery';
  if (p.includes('contract') || p.startsWith('rec')) return 'Contraction';
  if (p.includes('peak') || p.includes('boom')) return 'Peak';
  return phase.charAt(0).toUpperCase() + phase.slice(1);
};

// ============================================================
// LANDING CYCLE CONTEXT — single shared fetch for hero, ticker, badge
// ============================================================
const LandingCycleContext = React.createContext({ status: 'loading', data: null });

function LandingCycleProvider({ children }) {
  const [state, setState] = useState({ status: 'loading', data: null });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/economic/cycle`);
        if (cancelled) return;
        const d = res.data || {};
        // Prefer canonical_phase (matches what authenticated dashboard shows
        // via /economic/cycle/current). Fall back to basic phase.
        const canonicalRaw = d.canonical_phase || d.phase;
        if (!canonicalRaw) { setState({ status: 'error', data: null }); return; }
        const canonicalLower = String(canonicalRaw).toLowerCase();
        const basicLower = d.phase ? String(d.phase).toLowerCase() : null;
        const advancedLower = d.advanced_phase ? String(d.advanced_phase).toLowerCase() : null;
        // Divergence: basic vs advanced disagree (the canonical view is the
        // advanced one, so when they differ, the lagging signal is misleading).
        const divergent =
          !!(basicLower && advancedLower && basicLower !== advancedLower);
        // When divergent, the basic-phase recommended_sectors from the backend
        // would mislead users (e.g. "Technology" while we're calling Contraction).
        // Override with canonical-phase favored sectors from LANDING_CYCLE_CONTENT.
        const canonicalContent = LANDING_CYCLE_CONTENT[canonicalLower];
        const favored =
          divergent && canonicalContent
            ? canonicalContent.favor_sectors
            : (Array.isArray(d.recommended_sectors) ? d.recommended_sectors : (canonicalContent ? canonicalContent.favor_sectors : []));
        setState({
          status: 'ready',
          data: {
            phase: canonicalLower,
            phaseLabel: CYCLE_LABEL(canonicalLower),
            basicPhase: basicLower,
            basicPhaseLabel: basicLower ? CYCLE_LABEL(basicLower) : null,
            advancedPhase: advancedLower,
            advancedPhaseLabel: advancedLower ? CYCLE_LABEL(advancedLower) : null,
            divergent,
            lei: d.lei,
            cei: d.cei,
            recessionProbability: d.recession_probability,
            asOf: d.as_of,
            recommendedSectors: favored,
          },
        });
      } catch {
        if (!cancelled) setState({ status: 'error', data: null });
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return (
    <LandingCycleContext.Provider value={state}>
      {children}
    </LandingCycleContext.Provider>
  );
}

function useLandingCycle() {
  return React.useContext(LandingCycleContext);
}

// ============================================================
const LANDING_CYCLE_CONTENT = {
  expansion: {
    headline_top: 'The economy is expanding.',
    headline_bottom: 'Are you positioned to benefit?',
    favor_sectors: ['Technology', 'Financials', 'Industrials'],
  },
  contraction: {
    headline_top: 'The economy is contracting.',
    headline_bottom: 'Is your portfolio ready?',
    favor_sectors: ['Utilities', 'Healthcare', 'Consumer Staples'],
  },
  recession: {
    headline_top: 'The economy is contracting.',
    headline_bottom: 'Is your portfolio ready?',
    favor_sectors: ['Utilities', 'Healthcare', 'Consumer Staples'],
  },
  peak: {
    headline_top: 'Growth is slowing.',
    headline_bottom: 'Time to rotate before it turns.',
    favor_sectors: ['Energy', 'Materials', 'Healthcare'],
  },
  boom: {
    headline_top: 'Growth is slowing.',
    headline_bottom: 'Time to rotate before it turns.',
    favor_sectors: ['Energy', 'Materials', 'Healthcare'],
  },
  recovery: {
    headline_top: 'The recovery is underway.',
    headline_bottom: 'Position for what leads next.',
    favor_sectors: ['Consumer Discretionary', 'Industrials', 'Materials'],
  },
};

const HERO_STATIC_SUBHEADLINE =
  'PortfolioEngine analyzes 17 FRED indicators daily and shows you exactly which sectors to favor — in any market phase.';

// ============================================================
// DYNAMIC HERO HEADLINE — driven by current cycle phase
// ============================================================
function DynamicHeroHeadline() {
  const { status, data } = useLandingCycle();

  if (status === 'loading') {
    return (
      <>
        <h1
          data-testid="hero-headline-loading"
          className="text-4xl sm:text-5xl lg:text-6xl font-dm-sans font-bold text-gray-400 leading-tight"
        >
          Analyzing today&apos;s economic cycle...
        </h1>
        <p className="text-xl text-gray-500 max-w-2xl">{HERO_STATIC_SUBHEADLINE}</p>
      </>
    );
  }

  // On error: fall back to expansion-flavoured copy (better than blank)
  const phaseKey = data ? String(data.phase).toLowerCase() : 'expansion';
  const content = LANDING_CYCLE_CONTENT[phaseKey] || LANDING_CYCLE_CONTENT.expansion;

  return (
    <AnimatedHeadlineSplit
      topLine={content.headline_top}
      bottomLine={content.headline_bottom}
      subtext={HERO_STATIC_SUBHEADLINE}
    />
  );
}

// AnimatedHeadlineSplit — same character animation but for two-line emphasis (top plain, bottom bold teal)
function AnimatedHeadlineSplit({ topLine, bottomLine, subtext }) {
  const fullText = `${topLine} ${bottomLine}`;
  const totalChars = fullText.replace(/\s/g, '').length;
  const subDelay = totalChars * 0.025 + 0.2;
  let charIndex = 0;

  const renderLine = (line, color) => {
    const words = line.split(' ');
    return words.map((word, wi) => (
      <span key={wi} className="hero-word-mask">
        <span className="hero-word-inner">
          {word.split('').map((char, ci) => {
            const delay = (charIndex++) * 0.025;
            return (
              <span
                key={ci}
                className="hero-char"
                style={{ animationDelay: `${delay.toFixed(3)}s`, color }}
              >
                {char}
              </span>
            );
          })}
        </span>
      </span>
    ));
  };

  return (
    <>
      <h1
        className="text-4xl sm:text-5xl lg:text-6xl font-dm-sans font-bold leading-tight"
        data-testid="hero-headline"
        aria-label={fullText}
      >
        <span className="hero-headline-wrap" style={{ justifyContent: 'flex-start' }}>
          {renderLine(topLine, '#1f2937')}
        </span>
        <span className="hero-headline-wrap" style={{ justifyContent: 'flex-start' }}>
          {renderLine(bottomLine, '#1D9E75')}
        </span>
      </h1>
      <p
        className="hero-sub-fade text-xl text-gray-600 max-w-2xl"
        style={{ animationDelay: `${subDelay.toFixed(2)}s` }}
        data-testid="hero-subheadline"
      >
        {subtext}
      </p>
    </>
  );
}

// ============================================================
// HERO PRODUCT VISUAL — floating mockup frame with live phase badge
// ============================================================
const HERO_VISUAL_FALLBACKS = [
  '/tutorial-screenshots/hero-dashboard.png',
  '/tutorial-screenshots/cycle-analysis.png',
  '/tutorial-screenshots/health-score.png',
];

// ============================================================
// HERO PRODUCT COMPOSITION — Dynamic SVG mockup for the hero.
// Adapts to the current cycle phase from LandingCycleContext.
// Renders a stylized "live dashboard" surface (no static images)
// with phase-coloured accents, faux KPI rings, sparkline, and
// recommended-sectors chips. Pure SVG + CSS — no fetches of its own.
// ============================================================
function HeroProductComposition() {
  const { status, data } = useLandingCycle();

  // Phase-specific palette
  const phaseKey = data ? String(data.phase || '').toLowerCase() : 'expansion';
  const phaseLabel = data ? data.phaseLabel : 'Expansion';
  const accent = CYCLE_DOT_COLORS[phaseKey] || '#33cc99';
  // Sectors from canonical content (defensive override when divergent)
  const sectors = (data && data.recommendedSectors && data.recommendedSectors.length > 0)
    ? data.recommendedSectors.slice(0, 3)
    : ['Technology', 'Healthcare', 'Industrials'];
  const recProb = data && data.recessionProbability != null
    ? formatPctOneDecimal(data.recessionProbability)
    : '—';
  const lei = data && data.lei != null ? Number(data.lei).toFixed(3) : '—';
  const cei = data && data.cei != null ? Number(data.cei).toFixed(3) : '—';

  // Health-score ring fake-but-deterministic (78/100, dynamic only by phase tone)
  const healthScore = phaseKey === 'contraction' ? 72 : phaseKey === 'recovery' ? 81 : 78;
  const healthCircumference = 2 * Math.PI * 28; // radius 28
  const healthDash = (healthScore / 100) * healthCircumference;

  // Sparkline path — stylized monotone curve, never a real series
  const sparkPath = phaseKey === 'contraction'
    ? 'M0,40 L20,30 L40,38 L60,32 L80,42 L100,36 L120,46 L140,40 L160,52 L180,48 L200,58'
    : phaseKey === 'recovery'
      ? 'M0,55 L20,52 L40,50 L60,42 L80,38 L100,32 L120,28 L140,24 L160,20 L180,18 L200,15'
      : 'M0,45 L20,40 L40,38 L60,32 L80,30 L100,26 L120,28 L140,22 L160,20 L180,16 L200,12';

  return (
    <div
      className="hero-comp-wrap"
      data-testid="hero-product-visual"
      data-phase={phaseKey}
    >
      <div className="hero-comp-label">
        <span
          aria-hidden="true"
          style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: '#1D9E75', marginRight: 4 }}
        />
        Live dashboard · {status === 'loading' ? 'Loading…' : 'Updated today'}
      </div>

      <div className="hero-comp-frame">
        <svg
          className="hero-comp-svg"
          viewBox="0 0 560 380"
          xmlns="http://www.w3.org/2000/svg"
          role="img"
          aria-label={`PortfolioEngine dashboard mockup — ${phaseLabel} phase`}
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Window chrome */}
          <rect x="0" y="0" width="560" height="380" rx="10" fill="#ffffff" />
          <rect x="0" y="0" width="560" height="32" fill="#f8fafc" />
          <circle cx="16" cy="16" r="4.5" fill="#fb7185" />
          <circle cx="32" cy="16" r="4.5" fill="#fbbf24" />
          <circle cx="48" cy="16" r="4.5" fill="#34d399" />
          <rect x="200" y="9" width="160" height="14" rx="7" fill="#e5e7eb" />

          {/* Top bar — phase pill */}
          <g transform="translate(20, 50)">
            <rect width="180" height="28" rx="14" fill={accent} fillOpacity="0.10" stroke={accent} strokeOpacity="0.35" />
            <circle cx="14" cy="14" r="4" fill={accent}>
              <animate attributeName="opacity" values="0.4;1;0.4" dur="2.4s" repeatCount="indefinite" />
            </circle>
            <text x="26" y="18" fontFamily="Inter,system-ui,sans-serif" fontSize="11" fontWeight="600" fill="#1f2937">
              {phaseLabel} Phase
            </text>
            <text x="26" y="18" fontFamily="Inter,system-ui,sans-serif" fontSize="11" fill="#64748b" textAnchor="start" dx="78">
              · Updated today
            </text>
          </g>
          {/* Top bar — stat tiles */}
          <g transform="translate(220, 48)" fontFamily="Inter,system-ui,sans-serif">
            <rect width="100" height="34" rx="6" fill="#f8fafc" />
            <text x="10" y="14" fontSize="9" fill="#94a3b8" letterSpacing="0.05em">RECESSION</text>
            <text x="10" y="28" fontSize="13" fontWeight="700" fill="#1a2b5b">{recProb}</text>
            <rect x="110" width="100" height="34" rx="6" fill="#f8fafc" />
            <text x="120" y="14" fontSize="9" fill="#94a3b8" letterSpacing="0.05em">LEI</text>
            <text x="120" y="28" fontSize="13" fontWeight="700" fill="#0077ff">{lei}</text>
            <rect x="220" width="100" height="34" rx="6" fill="#f8fafc" />
            <text x="230" y="14" fontSize="9" fill="#94a3b8" letterSpacing="0.05em">CEI</text>
            <text x="230" y="28" fontSize="13" fontWeight="700" fill="#00aaff">{cei}</text>
          </g>

          {/* Left card — Health Score ring */}
          <g transform="translate(20, 100)">
            <rect width="240" height="160" rx="10" fill="#ffffff" stroke="#e5e7eb" />
            <text x="16" y="24" fontFamily="Inter,system-ui,sans-serif" fontSize="11" fontWeight="600" fill="#475569">Portfolio Health</text>
            <g transform="translate(120, 90)">
              <circle r="28" fill="none" stroke="#e5e7eb" strokeWidth="6" />
              <circle
                r="28"
                fill="none"
                stroke={healthScore >= 80 ? '#33cc99' : healthScore >= 60 ? '#BA7517' : '#E24B4A'}
                strokeWidth="6"
                strokeLinecap="round"
                strokeDasharray={`${healthDash} ${healthCircumference - healthDash}`}
                strokeDashoffset={healthCircumference / 4}
                transform="rotate(-90)"
              />
              <text textAnchor="middle" dy="6" fontFamily="Inter,system-ui,sans-serif" fontSize="20" fontWeight="700" fill="#1a2b5b">{healthScore}</text>
            </g>
            <text x="120" y="148" textAnchor="middle" fontFamily="Inter,system-ui,sans-serif" fontSize="10" fill="#64748b">/ 100 · {healthScore >= 80 ? 'Excellent' : healthScore >= 60 ? 'Good' : 'Needs review'}</text>
          </g>

          {/* Right card — Sparkline */}
          <g transform="translate(280, 100)">
            <rect width="260" height="160" rx="10" fill="#ffffff" stroke="#e5e7eb" />
            <text x="16" y="24" fontFamily="Inter,system-ui,sans-serif" fontSize="11" fontWeight="600" fill="#475569">Cycle Index — last 12 months</text>
            <g transform="translate(20, 60)">
              <path d={sparkPath} stroke={accent} strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              <path d={`${sparkPath} L200,90 L0,90 Z`} fill={accent} fillOpacity="0.10" />
              {/* Last point marker */}
              <circle r="3.5" cx="200" cy={phaseKey === 'contraction' ? 58 : phaseKey === 'recovery' ? 15 : 12} fill={accent}>
                <animate attributeName="r" values="3.5;5.5;3.5" dur="2.2s" repeatCount="indefinite" />
              </circle>
            </g>
          </g>

          {/* Bottom card — Favored Sectors */}
          <g transform="translate(20, 280)">
            <rect width="520" height="78" rx="10" fill="#ffffff" stroke="#e5e7eb" />
            <text x="16" y="24" fontFamily="Inter,system-ui,sans-serif" fontSize="11" fontWeight="600" fill="#475569">Favored sectors · {phaseLabel}</text>
            {sectors.map((s, i) => (
              <g key={i} transform={`translate(${16 + i * 168}, 38)`}>
                <rect width="156" height="28" rx="14" fill={accent} fillOpacity="0.08" stroke={accent} strokeOpacity="0.30" />
                <circle cx="14" cy="14" r="3.5" fill={accent} />
                <text x="26" y="18" fontFamily="Inter,system-ui,sans-serif" fontSize="11" fontWeight="500" fill="#1f2937">{s}</text>
              </g>
            ))}
          </g>
        </svg>
      </div>
    </div>
  );
}

function HeroProductVisual() {
  const { status, data } = useLandingCycle();
  const [srcIdx, setSrcIdx] = useState(0);
  const [allFailed, setAllFailed] = useState(false);

  const onImgError = () => {
    if (srcIdx < HERO_VISUAL_FALLBACKS.length - 1) {
      setSrcIdx(srcIdx + 1);
    } else {
      setAllFailed(true);
    }
  };

  const phaseKey = data ? String(data.phase).toLowerCase() : 'expansion';
  const dotColor = CYCLE_DOT_COLORS[phaseKey] || '#1D9E75';
  const phaseLabel = data ? data.phaseLabel : 'Expansion';

  return (
    <div className="hero-visual-wrap" data-testid="hero-product-visual">
      <div className="hero-visual-label">
        <span
          aria-hidden="true"
          style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: '#1D9E75', marginRight: 4 }}
        />
        Live dashboard · Updated today
      </div>

      <div className="hero-visual-frame">
        {allFailed ? (
          <div
            className="hero-visual-placeholder"
            style={{
              background: `linear-gradient(135deg, rgba(29,158,117,0.20) 0%, rgba(29,158,117,0.05) 100%)`,
            }}
          >
            <span>PortfolioEngine</span>
            <span style={{ fontSize: 13, opacity: 0.7, marginTop: 6 }}>Live cycle dashboard</span>
          </div>
        ) : (
          <img
            src={HERO_VISUAL_FALLBACKS[srcIdx]}
            alt="PortfolioEngine dashboard"
            onError={onImgError}
            loading="eager"
            className="hero-visual-img"
          />
        )}

        {status !== 'error' && (
          <div className="hero-visual-badge" data-testid="hero-visual-phase-badge">
            <span
              aria-hidden="true"
              style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: dotColor,
              }}
            />
            <span style={{ fontWeight: 600, color: '#1f2937' }}>{phaseLabel}</span>
            <span style={{ color: '#6b7280' }}>· Cycle active</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// DivergenceWhyLink — small inline link shown only when basic vs
// advanced cycle indicators disagree. The landing page surfaces the
// signal; the explanation lives inside the authenticated app.
// ============================================================
function DivergenceWhyLink() {
  const { status, data } = useLandingCycle();
  if (status !== 'ready' || !data || !data.divergent) return null;
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[12px] font-medium"
      data-testid="divergence-why-link"
      style={{
        background: 'rgba(226, 75, 74, 0.08)',
        border: '1px solid rgba(226, 75, 74, 0.25)',
        borderRadius: '999px',
        padding: '4px 10px',
        color: '#b91c1c',
      }}
    >
      <AlertCircle className="w-3.5 h-3.5" aria-hidden="true" />
      <span>Lagging signals disagree</span>
      <Link
        to="/login"
        className="underline decoration-dotted underline-offset-2 hover:opacity-80"
        data-testid="divergence-why-cta"
      >
        See why →
      </Link>
    </span>
  );
}

function EconomicCycleBadge() {
  const { status, data } = useLandingCycle();

  if (status === 'error') return null;

  if (status === 'loading' || !data) {
    return (
      <div
        className="inline-flex items-center gap-2 text-[13px]"
        style={{
          background: 'rgba(148, 163, 184, 0.10)',
          border: '1px solid rgba(148, 163, 184, 0.25)',
          borderRadius: '999px',
          padding: '4px 12px',
          color: '#64748b',
        }}
        data-testid="hero-cycle-badge-loading"
      >
        <span
          aria-hidden="true"
          style={{
            display: 'inline-block',
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: '#94a3b8',
          }}
        />
        Analyzing market conditions...
      </div>
    );
  }

  const key = String(data.phase || '').toLowerCase();
  const dotColor = CYCLE_DOT_COLORS[key] || '#1D9E75';
  const label = data.phaseLabel;

  return (
    <div
      className="inline-flex items-center gap-2 text-[13px]"
      style={{
        background: 'rgba(29, 158, 117, 0.08)',
        border: '1px solid rgba(29, 158, 117, 0.2)',
        borderRadius: '999px',
        padding: '4px 12px',
        color: '#4b5563',
      }}
      data-testid="hero-cycle-badge"
    >
      <span
        aria-hidden="true"
        style={{
          display: 'inline-block',
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: dotColor,
        }}
      />
      <span>
        <span style={{ fontWeight: 600, color: '#1f2937' }} data-testid="hero-cycle-badge-phase">
          {label}
        </span>
        {' · Economic cycle · Updated today'}
      </span>
    </div>
  );
}


// ============================================================
// SHARED LANDING CSS — atmospheric depth, scroll reveals, ticker
// ============================================================
const LANDING_FX_CSS = `
/* --- Hero product composition (dynamic SVG mockup) --- */
.hero-comp-wrap {
  position: relative;
  width: 100%;
  padding-bottom: 16px;
}
.hero-comp-label {
  font-size: 11px;
  color: #1D9E75;
  letter-spacing: 0.5px;
  font-weight: 500;
  text-transform: uppercase;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
}
.hero-comp-frame {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(29,158,117,0.20);
  box-shadow:
    0 24px 80px rgba(0,0,0,0.15),
    0 8px 32px rgba(29,158,117,0.10);
  background: white;
  aspect-ratio: 560 / 380;
  transform: rotate(-2deg);
  transition: transform 0.4s cubic-bezier(0.16,1,0.3,1);
}
.hero-comp-frame:hover {
  transform: rotate(0deg);
}
.hero-comp-svg {
  display: block;
  width: 100%;
  height: 100%;
}
@media (max-width: 1023px) {
  .hero-comp-frame { transform: none; }
  .hero-comp-frame:hover { transform: none; }
}
@media (prefers-reduced-motion: reduce) {
  .hero-comp-frame { transform: none; transition: none; }
  .hero-comp-frame:hover { transform: none; }
}

/* --- Hero product visual (floating mockup) --- */
.hero-visual-wrap {
  position: relative;
  width: 100%;
  padding-bottom: 16px; /* room for the floating phase badge */
}
.hero-visual-label {
  font-size: 11px;
  color: #1D9E75;
  letter-spacing: 0.5px;
  font-weight: 500;
  text-transform: uppercase;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
}
.hero-visual-frame {
  position: relative;
  border-radius: 12px;
  border: 1px solid rgba(29,158,117,0.20);
  box-shadow:
    0 24px 80px rgba(0,0,0,0.15),
    0 8px 32px rgba(29,158,117,0.10);
  background: white;
  aspect-ratio: 16 / 10;
  transform: rotate(-2deg);
  transition: transform 0.4s cubic-bezier(0.16,1,0.3,1);
}
.hero-visual-frame:hover {
  transform: rotate(0deg);
}
.hero-visual-img,
.hero-visual-placeholder {
  border-radius: 12px;
  overflow: hidden;
}
.hero-visual-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.hero-visual-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #1D9E75;
  font-weight: 600;
  font-size: 22px;
}
.hero-visual-badge {
  position: absolute;
  bottom: -16px;
  left: 16px;
  background: white;
  border-radius: 8px;
  padding: 8px 12px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.12);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  white-space: nowrap;
  z-index: 2;
}
@media (max-width: 1023px) {
  .hero-visual-frame { transform: none; }
  .hero-visual-frame:hover { transform: none; }
}
@media (prefers-reduced-motion: reduce) {
  .hero-visual-frame { transform: none; transition: none; }
  .hero-visual-frame:hover { transform: none; }
}

/* --- Ticker bar --- */
.ticker-bar {
  position: relative;
  width: 100%;
  height: 36px;
  overflow: hidden;
  background: rgba(0,0,0,0.02);
  border-top: 1px solid rgba(0,0,0,0.06);
  border-bottom: 1px solid rgba(0,0,0,0.06);
  font-size: 13px;
  color: #4b5563;
}
/* Dark variant when used at the very top of the page */
.ticker-bar.ticker-bar-top {
  background: rgba(6, 78, 59, 0.95);
  border-top: none;
  border-bottom: 1px solid rgba(255,255,255,0.10);
  color: rgba(255,255,255,0.9);
  position: relative;
  z-index: 60;
}
.ticker-bar.ticker-bar-top .ticker-segment {
  border-right: 1px solid rgba(255,255,255,0.08);
}
.ticker-bar.ticker-bar-top .ticker-label {
  color: rgba(255,255,255,0.55);
}
.ticker-bar.ticker-bar-top .ticker-value {
  color: rgba(255,255,255,0.95);
}
.ticker-track {
  display: flex;
  width: max-content;
  align-items: center;
  height: 100%;
  white-space: nowrap;
  will-change: transform;
}
@media (prefers-reduced-motion: no-preference) {
  .ticker-track {
    animation: tickerScroll 45s linear infinite;
  }
  .ticker-bar:hover .ticker-track {
    animation-play-state: paused;
  }
}
@keyframes tickerScroll {
  from { transform: translateX(0); }
  to   { transform: translateX(-33.3333%); }
}
.ticker-segment {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 18px;
  border-right: 1px solid rgba(0,0,0,0.06);
}
.ticker-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.ticker-label {
  color: #9ca3af;
  font-weight: 500;
  letter-spacing: 0.02em;
}
.ticker-value {
  color: #1f2937;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

/* --- Section heading wipe in --- */
.section-heading-wipe {
  display: inline-block;
  clip-path: inset(0 100% 0 0);
}
@media (prefers-reduced-motion: no-preference) {
  .section-heading-wipe.in-view {
    animation: sectionWipe 0.65s cubic-bezier(0.16,1,0.3,1) forwards;
  }
}
@keyframes sectionWipe {
  to { clip-path: inset(0 0% 0 0); }
}
@media (prefers-reduced-motion: reduce) {
  .section-heading-wipe { clip-path: inset(0 0% 0 0); }
}

/* --- Feature card depth + hover choreography --- */
.feat-card {
  position: relative;
  background: white;
  border-radius: 12px;
  border: 1px solid rgba(0,0,0,0.08);
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  overflow: hidden;
  transition:
    transform 0.3s cubic-bezier(0.16,1,0.3,1),
    box-shadow 0.3s cubic-bezier(0.16,1,0.3,1),
    border-color 0.3s ease;
  opacity: 0;
  transform: translateY(24px);
}
.feat-card.in-view {
  opacity: 1;
  transform: translateY(0);
}
.feat-card::before {
  content: '';
  position: absolute;
  left: 0; top: 0; bottom: 0;
  width: 3px;
  background: var(--feat-color, #1D9E75);
  opacity: 0.6;
  transition: opacity 0.3s ease;
}
.feat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0,0,0,0.12);
}
.feat-card:hover::before {
  opacity: 1;
}
.feat-card-title {
  position: relative;
  display: inline-block;
}
.feat-card-title::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: -3px;
  height: 2px;
  width: 0;
  background: var(--feat-color, #1D9E75);
  transition: width 0.4s cubic-bezier(0.16,1,0.3,1);
}
.feat-card:hover .feat-card-title::after {
  width: 100%;
}
@media (prefers-reduced-motion: reduce) {
  .feat-card { opacity: 1; transform: none; transition: none; }
  .feat-card:hover { transform: none; }
  .feat-card-title::after { display: none; }
}

/* --- How-it-works steps stagger + connector line --- */
.hiw-step {
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.5s ease, transform 0.5s cubic-bezier(0.16,1,0.3,1);
}
.hiw-step.in-view {
  opacity: 1;
  transform: translateY(0);
}
.hiw-connector {
  position: absolute;
  top: 32px;
  left: 16.66%;
  right: 16.66%;
  height: 2px;
  background: linear-gradient(
    90deg,
    rgba(29,158,117,0) 0%,
    rgba(29,158,117,0.35) 15%,
    rgba(29,158,117,0.35) 85%,
    rgba(29,158,117,0) 100%
  );
  pointer-events: none;
  z-index: 0;
}
@media (max-width: 768px) { .hiw-connector { display: none; } }
@media (prefers-reduced-motion: reduce) {
  .hiw-step { opacity: 1; transform: none; }
}

/* --- POPULAR badge shimmer --- */
.popular-shimmer {
  background: linear-gradient(
    90deg,
    #1D9E75 0%,
    #25c492 50%,
    #1D9E75 100%
  );
  background-size: 200% auto;
  color: white;
}
@media (prefers-reduced-motion: no-preference) {
  .popular-shimmer { animation: popularShimmer 3s linear infinite; }
}
@keyframes popularShimmer {
  0%   { background-position: -200% center; }
  100% { background-position: 200% center; }
}
`;

// ============================================================
// useInView — IntersectionObserver hook for scroll-triggered animations
// ============================================================
function useInView(options = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: options.threshold ?? 0.15, rootMargin: options.rootMargin ?? '0px 0px -10% 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [options.threshold, options.rootMargin]);
  return [ref, inView];
}

// WipeText — inline span with scroll-triggered clip-path wipe (preserves parent h2/h3 styling)
function WipeText({ children, threshold = 0.4 }) {
  const [ref, inView] = useInView({ threshold });
  return (
    <span ref={ref} className={`section-heading-wipe ${inView ? 'in-view' : ''}`}>
      {children}
    </span>
  );
}

// ============================================================
// TICKER BAR — live cycle data from shared LandingCycleContext (top of page, dark theme)
// ============================================================
function formatPctOneDecimal(v) {
  if (v == null || isNaN(v)) return '–';
  const n = Number(v);
  const pct = n <= 1 ? n * 100 : n;
  return `${pct.toFixed(1)}%`;
}

function formatTodayLabel(asOf) {
  try {
    const d = asOf ? new Date(asOf) : new Date();
    if (isNaN(d.getTime())) return 'Today';
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return 'Today';
  }
}

function TickerBar() {
  const { status, data } = useLandingCycle();

  if (status === 'error') return null;

  const renderSegment = () => {
    if (status === 'loading' || !data) {
      return (
        <>
          <span className="ticker-segment">
            <span className="ticker-dot" style={{ background: 'rgba(255,255,255,0.5)' }} />
            <span className="ticker-value">Analyzing market conditions</span>
          </span>
          <span className="ticker-segment">
            <span className="ticker-label">PortfolioEngine</span>
            <span className="ticker-value">Real-time economic cycle analysis</span>
          </span>
        </>
      );
    }
    const phaseKey = String(data.phase).toLowerCase();
    const dotColor = CYCLE_DOT_COLORS[phaseKey] || '#1D9E75';
    const phaseLabel = data.phaseLabel;
    const sectors = (data.recommendedSectors || []).slice(0, 3);
    const leiDisplay = data.lei == null ? '–' : Number(data.lei).toFixed(3);
    const ceiDisplay = data.cei == null ? '–' : Number(data.cei).toFixed(3);
    const recDisplay = data.recessionProbability == null
      ? '–'
      : formatPctOneDecimal(data.recessionProbability);

    return (
      <>
        <span className="ticker-segment">
          <span className="ticker-dot" style={{ background: dotColor }} />
          <span className="ticker-value">{phaseLabel} Phase</span>
        </span>
        <span className="ticker-segment">
          <span className="ticker-label">Recession Risk</span>
          <span className="ticker-value">{recDisplay}</span>
        </span>
        <span className="ticker-segment">
          <span className="ticker-label">LEI</span>
          <span className="ticker-value">{leiDisplay}</span>
          <span className="ticker-label" style={{ marginLeft: 8 }}>CEI</span>
          <span className="ticker-value">{ceiDisplay}</span>
        </span>
        <span className="ticker-segment">
          <span className="ticker-label">Updated</span>
          <span className="ticker-value">{formatTodayLabel(data.asOf)}</span>
        </span>
        {sectors.length > 0 && (
          <span className="ticker-segment">
            <span className="ticker-label">Favored</span>
            {sectors.map((s, i) => (
              <span key={i} className="ticker-value" style={{ color: dotColor }}>
                {s}{i < sectors.length - 1 ? ' ·' : ''}
              </span>
            ))}
          </span>
        )}
      </>
    );
  };

  // Render 3 copies for seamless scrolling loop
  return (
    <div className="ticker-bar ticker-bar-top" data-testid="hero-ticker-bar" role="marquee" aria-label="Live economic cycle data">
      <div className="ticker-track">
        {[0, 1, 2].map((i) => (
          <div key={i} style={{ display: 'inline-flex', alignItems: 'center', height: '100%', flexShrink: 0 }}>
            {renderSegment()}
          </div>
        ))}
      </div>
    </div>
  );
}


// ============================================================
// FEATURE TOUR — Interactive landing-page module selector (6 features)
// ============================================================
const FEATURE_TOUR_IDS = ['cycle', 'portfolio', 'health_score', 'evaluator', 'rebalancing', 'ai_builder'];

const FEATURE_TOUR_IMAGE_MAP = {
  cycle:        ['hero-cycle-analysis.png', 'hero-dashboard.png'],
  portfolio:    ['portfolio-builder-search.png', 'hero-dashboard.png'],
  health_score: ['health-detail-page.png', 'hero-dashboard.png'],
  evaluator:    ['hero-evaluator.png', 'hero-dashboard.png'],
  rebalancing:  ['rebalancing-results.png', 'hero-dashboard.png'],
  ai_builder:   ['ai-builder-result.png', 'hero-dashboard.png'],
};

function FeatureTourImage({ module }) {
  const candidates = FEATURE_TOUR_IMAGE_MAP[module.id] || [`${module.primary_screenshot}.png`];
  const [idx, setIdx] = useState(0);
  const [allFailed, setAllFailed] = useState(false);

  // reset state when active module changes
  useEffect(() => {
    setIdx(0);
    setAllFailed(false);
  }, [module.id]);

  if (allFailed) {
    return (
      <div
        className="w-full h-full flex flex-col items-center justify-center text-white text-2xl font-medium px-6 text-center"
        style={{
          background: `linear-gradient(135deg, ${module.feature_color} 0%, ${module.feature_color}10 100%)`,
        }}
      >
        <span className="drop-shadow">{module.title}</span>
        <span className="text-xs uppercase tracking-wider opacity-80 mt-3 font-mono">PortfolioEngine preview</span>
      </div>
    );
  }

  return (
    <img
      src={`/tutorial-screenshots/${candidates[idx]}`}
      alt={module.title}
      loading="lazy"
      onError={() => {
        if (idx < candidates.length - 1) setIdx(idx + 1);
        else setAllFailed(true);
      }}
      className="w-full h-full object-cover"
      key={candidates[idx]}
    />
  );
}

function FeatureTour() {
  const navigate = useNavigate();
  const [activeIdx, setActiveIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  // Filter tutorialModules to the 6 IDs in FEATURE_TOUR_IDS, preserving order
  const tourModules = FEATURE_TOUR_IDS
    .map((id) => tutorialModules.find((m) => m.id === id))
    .filter(Boolean);

  const isAuthed = !!(typeof window !== 'undefined' && getSessionToken());
  const active = tourModules[activeIdx];

  // Auto-rotate
  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => {
      setActiveIdx((i) => (i + 1) % tourModules.length);
    }, 5000);
    return () => clearInterval(id);
  }, [paused, tourModules.length]);

  if (!active) return null;

  const planLabelMap = { book_bundle: 'Book Bundle', premium: 'Premium' };
  const planBadgeClass = {
    book_bundle: 'bg-amber-100 text-amber-800',
    premium: 'bg-teal-100 text-teal-800',
  };

  return (
    <section id="feature-tour" className="py-20 bg-white" data-testid="feature-tour">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-4xl font-dm-sans font-bold text-forest-900 mb-3">
            <WipeText>Everything you need to invest with the cycle</WipeText>
          </h2>
          <p className="text-lg text-gray-600">Click any feature to see it in action</p>
        </div>

        {/* Tab strip */}
        <div
          className="flex gap-2 overflow-x-auto pb-3 mb-8 -mx-4 px-4 scrollbar-thin"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {tourModules.map((m, idx) => {
            const isActive = idx === activeIdx;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => {
                  setActiveIdx(idx);
                  setPaused(true);
                }}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  isActive
                    ? 'text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                style={isActive ? { backgroundColor: m.feature_color } : undefined}
                data-testid={`feature-tour-tab-${m.id}`}
              >
                {m.title}
              </button>
            );
          })}
        </div>

        {/* Active module display */}
        <div
          className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-center"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          data-testid="feature-tour-display"
        >
          <div className="lg:col-span-3">
            <div
              className="relative aspect-[16/10] rounded-xl shadow-2xl overflow-hidden border border-gray-200 transition-opacity duration-500"
              style={{
                transform: 'perspective(1500px) rotateY(-2deg) rotateX(2deg)',
                transformOrigin: 'center center',
              }}
            >
              <FeatureTourImage module={active} />
            </div>
          </div>

          <div className="lg:col-span-2 space-y-3">
            <div className="flex items-center gap-2">
              <span
                className="inline-block w-3 h-3 rounded-full"
                style={{ backgroundColor: active.feature_color }}
                aria-hidden="true"
              />
              <span className="text-xs uppercase tracking-wider text-gray-500 font-mono">
                {String(activeIdx + 1).padStart(2, '0')} / {tourModules.length}
              </span>
              {planLabelMap[active.plan_required] && (
                <span
                  className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${planBadgeClass[active.plan_required]}`}
                >
                  {planLabelMap[active.plan_required]}
                </span>
              )}
            </div>
            <h3 className="text-2xl font-bold text-gray-900">{active.title}</h3>
            <p className="text-gray-500">{active.subtitle}</p>
            <p className="text-gray-700 leading-relaxed">{active.short_copy}</p>
            <div className="flex flex-wrap gap-3 pt-3">
              <button
                type="button"
                onClick={() => navigate(isAuthed ? active.cta_link : '/login')}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-sm font-medium shadow-md hover:shadow-lg transition"
                style={{ backgroundColor: active.feature_color }}
                data-testid="feature-tour-primary-cta"
              >
                {isAuthed ? 'Open' : 'Try it free'}
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => navigate('/learn')}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50"
                data-testid="feature-tour-secondary-cta"
              >
                See all features
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


// ============================================================
// HowItWorksGrid — 3 steps with stagger entrance + horizontal connector line
// ============================================================
function HowItWorksGrid() {
  const steps = [
    { n: 1, title: 'Define your profile', desc: 'Answer a few simple questions about your goals and risk tolerance so we can tailor our recommendations.' },
    { n: 2, title: 'Build your portfolio', desc: "Create or import your investment portfolio. We'll help you balance it across stocks, bonds, commodities and crypto with just a few clicks." },
    { n: 3, title: 'Get cycle-based guidance', desc: 'See which phase the economy is in—expansion, peak, contraction or recovery—and receive personalized suggestions to adjust your portfolio.' },
  ];
  return (
    <div className="relative grid md:grid-cols-3 gap-8">
      <span className="hiw-connector" aria-hidden="true" />
      {steps.map((s, i) => (
        <HiwStep key={s.n} step={s} index={i} />
      ))}
    </div>
  );
}

function HiwStep({ step, index }) {
  const [ref, inView] = useInView({ threshold: 0.3 });
  return (
    <div
      ref={ref}
      className={`hiw-step text-center relative z-10 ${inView ? 'in-view' : ''}`}
      style={{ transitionDelay: `${index * 0.12}s` }}
    >
      <div className="w-16 h-16 bg-white border-2 border-forest-200 rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
        <span className="text-2xl font-bold text-forest-900">{step.n}</span>
      </div>
      <h3 className="text-xl font-bold text-forest-900 mb-2">{step.title}</h3>
      <p className="text-gray-600">{step.desc}</p>
    </div>
  );
}


// ============================================================
// FeatureBenefitsGrid — 4 cards with depth, hover choreography, and stagger entrance
// ============================================================
function FeatureBenefitsGrid() {
  const items = [
    { icon: Activity, color: '#1D9E75', title: 'Know the economic cycle', desc: 'Understand whether the market is growing or slowing down, and what that means for your investments.' },
    { icon: Target,   color: '#378ADD', title: 'Diversify with confidence', desc: 'Use smart allocations to spread your money across sectors and asset classes without needing to be an expert.' },
    { icon: Clock,    color: '#F39C12', title: 'Test your strategy', desc: 'Backtest your portfolio against past market cycles to see how it might perform over time.' },
    { icon: BookOpen, color: '#BA7517', title: 'Learn as you go', desc: 'Explore our educational hub to grasp the basics of investing, risk management and different asset types.' },
  ];
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
      {items.map((it, i) => (
        <BenefitCard key={i} {...it} index={i} />
      ))}
    </div>
  );
}

function BenefitCard({ icon: Icon, color, title, desc, index }) {
  const [ref, inView] = useInView({ threshold: 0.2 });
  return (
    <div
      ref={ref}
      className={`feat-card p-6 ${inView ? 'in-view' : ''}`}
      style={{
        '--feat-color': color,
        transitionDelay: `${index * 0.08}s`,
      }}
      data-testid={`benefit-card-${index}`}
    >
      <div
        className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
        style={{ background: `${color}1A`, color }}
      >
        <Icon className="h-8 w-8" />
      </div>
      <h3 className="text-xl font-bold text-forest-900 mb-2">
        <span className="feat-card-title">{title}</span>
      </h3>
      <p className="text-gray-600">{desc}</p>
    </div>
  );
}


function LandingPage() {
  const navigate = useNavigate();

  const handleLogin = () => {
    navigate('/login');
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <LandingCycleProvider>
      <style>{HERO_ANIM_CSS}</style>
      <style>{LANDING_FX_CSS}</style>
    <div className="min-h-screen relative" style={{ background: 'linear-gradient(180deg, #ffffff 0%, rgba(29,158,117,0.025) 40%, #ffffff 100%)' }}>
      {/* Full-page neural mesh + grain backdrop — sits behind every section */}
      <NetworkBackdrop opacity={0.10} color="#00aaff" animate={true} />
      <div className="landing-mesh-grain" aria-hidden="true" />
      <div className="relative" style={{ zIndex: 1 }}>
      {/* Live data ticker — TOP OF PAGE */}
      <TickerBar />

      {/* Header - KEPT AS IS */}
      <header className="border-b bg-white/70 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-logo-link">
            <img
              src="/logo-nav.png"
              alt=""
              className="h-10 w-auto"
              data-testid="brand-logo"
            />
            <span className="text-2xl sm:text-[1.65rem] font-dm-sans font-bold tracking-tight leading-none">
              <span style={{ color: '#1a2b5b' }}>Portfolio</span><span style={{ color: '#00aaff' }}>Engine</span>
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <a href="#features" className="hidden sm:block text-gray-600 hover:text-forest-900">Features</a>
            <a href="#pricing" className="hidden sm:block text-gray-600 hover:text-forest-900">Pricing</a>
            <button
              data-testid="landing-login-btn"
              onClick={handleLogin}
              className="px-6 py-2 text-white rounded-lg transition-all"
              style={{ background: BRAND.navy }}
              onMouseEnter={(e) => { e.currentTarget.style.background = BRAND.navyDeep; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = BRAND.navy; }}
            >
              Sign In
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section — 2-col with product visual */}
      <section className="hero-section max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid lg:grid-cols-[55%_45%] gap-10 lg:gap-12 items-center">
          {/* Left column: dynamic copy + CTAs */}
          <div className="space-y-7 text-left">
            <DynamicHeroHeadline />

            {/* Live economic cycle badge + divergence "See why" link */}
            <div className="flex flex-wrap items-center gap-3">
              <EconomicCycleBadge />
              <DivergenceWhyLink />
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-2">
              <button
                data-testid="landing-get-started-btn"
                onClick={handleLogin}
                className="px-8 py-4 bg-brand-navy text-white rounded-xl text-lg font-semibold hover:bg-brand-navy-dark transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
              >
                Get Started Free <ArrowRight className="h-5 w-5" />
              </button>
              <button
                onClick={() => scrollToSection('how-it-works')}
                className="px-8 py-4 border-2 border-forest-900 text-forest-900 rounded-xl text-lg font-semibold hover:bg-forest-50 transition-all flex items-center justify-center gap-2"
              >
                Learn How It Works
              </button>
            </div>

            {/* User type notes */}
            <div className="grid sm:grid-cols-2 gap-4 pt-2">
              <div className="flex items-center gap-2 text-gray-600 bg-forest-50 rounded-lg px-4 py-3">
                <Users className="h-5 w-5 text-forest-600 flex-shrink-0" />
                <span className="text-sm"><strong>New to investing?</strong> We&apos;ll guide you step by step.</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600 bg-forest-50 rounded-lg px-4 py-3">
                <Zap className="h-5 w-5 text-forest-600 flex-shrink-0" />
                <span className="text-sm"><strong>Want to improve?</strong> Try our backtesting tools.</span>
              </div>
            </div>

            {/* Trust indicators */}
            <div className="flex items-center gap-6 pt-1 flex-wrap">
              <div className="flex items-center gap-2 text-gray-500">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span className="text-sm">Free to Start</span>
              </div>
              <div className="flex items-center gap-2 text-gray-500">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span className="text-sm">No Credit Card</span>
              </div>
              <div className="flex items-center gap-2 text-gray-500">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span className="text-sm">Real Data</span>
              </div>
            </div>
          </div>

          {/* Right column: hero product composition (dynamic SVG mockup) */}
          <div className="hidden lg:block lg:pl-4">
            <HeroProductComposition />
          </div>
          <div className="lg:hidden">
            <HeroProductComposition />
          </div>
        </div>
      </section>

      {/* How It Works — early proof of process */}
      <section id="how-it-works" className="py-20 bg-gray-50/60 backdrop-blur-[2px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title font-dm-sans text-forest-900 mb-4"><WipeText>How it works</WipeText></h2>
          </div>
          <HowItWorksGrid />
        </div>
      </section>

      {/* Combined "See it in action" — single source of truth for product visuals */}
      <section id="product-preview" className="py-20" data-testid="product-preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="section-title font-dm-sans text-forest-900 mb-4"><WipeText>See PortfolioEngine in action</WipeText></h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Real screens from the platform. Hover any feature on the left to switch the view.
            </p>
          </div>
          <MockupShowcase />
        </div>
      </section>

      {/* Pricing Preview - UPDATED COPY */}
      <section id="pricing" className="bg-gray-50/60 backdrop-blur-[2px] py-20">
        <style>{`
          .pricing-card {
            transition: transform 0.3s cubic-bezier(0.16,1,0.3,1), box-shadow 0.3s cubic-bezier(0.16,1,0.3,1);
            will-change: transform;
          }
          .pricing-card:hover {
            transform: translateY(-6px);
            box-shadow: 0 16px 48px rgba(0, 0, 0, 0.12);
          }
          .pricing-card-premium {
            position: relative;
            transform: scale(1.03) translateY(-4px);
            background: linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(29,158,117,0.03) 100%);
            border: 1.5px solid rgba(29,158,117,0.35);
            box-shadow:
              0 0 0 1px rgba(29,158,117,0.10),
              0 4px 16px rgba(29,158,117,0.08),
              0 16px 48px rgba(0,0,0,0.08),
              inset 0 1px 0 rgba(255,255,255,0.8);
          }
          .pricing-card-premium:hover {
            transform: scale(1.03) translateY(-8px);
            border-color: rgba(29,158,117,0.55);
            box-shadow:
              0 0 0 1px rgba(29,158,117,0.20),
              0 8px 24px rgba(29,158,117,0.12),
              0 24px 64px rgba(0,0,0,0.12),
              inset 0 1px 0 rgba(255,255,255,0.9);
          }
          @media (prefers-reduced-motion: reduce) {
            .pricing-card,
            .pricing-card-premium {
              animation: none !important;
              transition: none !important;
            }
          }
        `}</style>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title font-dm-sans text-forest-900 mb-4"><WipeText>Simple Pricing</WipeText></h2>
            <p className="text-xl text-gray-600">Start free, upgrade when you're ready</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Free Tier */}
            <div className="pricing-card bg-white dark:bg-gray-800 rounded-2xl p-8 border border-gray-200" data-testid="pricing-card-free">
              <h3 className="text-2xl font-bold text-forest-900">Free</h3>
              <p className="text-gray-500 mb-4">Perfect for your first portfolio analysis</p>
              <p className="text-4xl font-bold text-forest-900">$0</p>
              <div className="mb-6" />
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-gray-600">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  1 Portfolio
                </li>
                <li className="flex items-center gap-2 text-gray-600">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Up to 5 Instruments
                </li>
                <li className="flex items-center gap-2 text-gray-600">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Risk Assessment
                </li>
                <li className="flex items-center gap-2 text-gray-600">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Cycle-based insights
                </li>
                <li className="flex items-center gap-2 text-gray-600">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Market Analysis
                </li>
              </ul>
              <button
                onClick={handleLogin}
                className="w-full py-3 rounded-lg font-semibold transition-all border-2 border-forest-900 text-forest-900 hover:bg-forest-50"
              >
                Get Started
              </button>
            </div>

            {/* Book Bundle Tier */}
            <div className="pricing-card bg-white dark:bg-gray-800 rounded-2xl p-8 border border-gray-200" data-testid="pricing-card-book">
              <h3 className="text-2xl font-bold text-forest-900">Book Bundle</h3>
              <p className="text-gray-500 mb-4">One-time purchase</p>
              <p className="text-4xl font-bold text-forest-900">$29.99</p>
              <div className="mb-6" />
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-gray-600">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  1 Portfolio
                </li>
                <li className="flex items-center gap-2 text-gray-600">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Up to 15 Instruments
                </li>
                <li className="flex items-center gap-2 text-gray-600">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Complete eBook included
                </li>
                <li className="flex items-center gap-2 text-gray-600">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Basic backtesting
                </li>
                <li className="flex items-center gap-2 text-gray-600">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Basic rebalancing
                </li>
              </ul>
              <button
                onClick={handleLogin}
                className="w-full py-3 rounded-lg font-semibold transition-all border-2 border-forest-900 text-forest-900 hover:bg-forest-50"
              >
                Get Started
              </button>
            </div>

            {/* Premium Tier */}
            <div className="pricing-card pricing-card-premium bg-white dark:bg-gray-800 rounded-2xl p-8" data-testid="pricing-card-premium">
              <span
                className="popular-shimmer inline-block px-3 py-1 text-xs font-bold rounded-full mb-4"
                data-testid="pricing-popular-badge"
              >
                POPULAR
              </span>
              <h3 className="text-2xl font-bold text-forest-900">Premium</h3>
              <p className="text-gray-500 mb-4">Full access</p>
              <p className="text-4xl font-bold text-forest-900">$9.99/mo</p>
              <p className="text-xs text-gray-400">billed annually at $119.88</p>
              <p className="text-sm text-gray-500 mb-4">or $149.99 lifetime</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-gray-600">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Unlimited Portfolios
                </li>
                <li className="flex items-center gap-2 text-gray-600">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Unlimited Instruments
                </li>
                <li className="flex items-center gap-2 text-gray-600">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Advanced heatmaps & correlations
                </li>
                <li className="flex items-center gap-2 text-gray-600">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Exports, alerts & AI insights
                </li>
                <li className="flex items-center gap-2 text-gray-600">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Complete eBook included
                </li>
              </ul>
              <button
                onClick={handleLogin}
                className="w-full py-3 rounded-lg font-semibold transition-all bg-brand-navy text-white hover:bg-brand-navy-dark"
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA — Phase-aware copy */}
      <FinalCTA navigateToLogin={handleLogin} navigateToTour={() => scrollToSection('feature-tour')} />

      {/* Footer - methodology disclaimer + Terms & Privacy links */}
      <footer className="bg-brand-navy text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-forest-200 text-xs leading-relaxed text-center max-w-3xl mx-auto mb-8">
            Our cycle engine analyzes trusted indicators like the yield curve and inflation from sources such as the Federal Reserve. We use this data to identify the current phase of the economic cycle and suggest sector weights. All recommendations are for educational purposes only; PortfolioEngine is not a registered financial advisor.
          </p>
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center gap-3 mb-4 md:mb-0">
              <img src="/logo-nav.png" alt="" className="h-10 w-auto" style={{ filter: 'brightness(1.5)' }} />
              <span className="text-xl font-bold tracking-tight leading-none">
                <span className="text-white">Portfolio</span><span style={{ color: '#00aaff' }}>Engine</span>
              </span>
            </div>
            <div className="flex flex-col items-center md:items-end gap-2">
              <div className="flex items-center gap-4 text-sm">
                <a href="/terms" className="text-forest-200 hover:text-white transition-colors">Terms of Service</a>
                <span className="text-forest-400">|</span>
                <a href="/privacy" className="text-forest-200 hover:text-white transition-colors">Privacy Policy</a>
              </div>
              <p className="text-forest-200 text-sm">
                &copy; 2025 PortfolioEngine. Educational purposes only. Not financial advice.
              </p>
            </div>
          </div>
        </div>
      </footer>
      </div>
    </div>
    </LandingCycleProvider>
  );
}

// ============================================================
// FINAL CTA — Phase-aware copy reads from LandingCycleContext
// ============================================================
function FinalCTA({ navigateToLogin, navigateToTour }) {
  const { status, data } = useLandingCycle();
  const phaseKey = status === 'ready' && data ? String(data.phase).toLowerCase() : 'expansion';
  const content = LANDING_CYCLE_CONTENT[phaseKey] || LANDING_CYCLE_CONTENT.expansion;

  return (
    <section className="py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="section-title font-dm-sans text-forest-900 mb-3">
          {content.headline_top}
        </h2>
        <h2 className="section-title font-dm-sans mb-6" style={{ color: '#1D9E75' }}>
          {content.headline_bottom}
        </h2>
        <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
          Build a portfolio aligned to today&apos;s cycle in minutes. Free to start, no credit card required.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={navigateToLogin}
            className="px-10 py-5 bg-brand-navy text-white rounded-xl text-xl font-semibold hover:bg-brand-navy-dark transition-all shadow-lg"
            data-testid="landing-final-cta-primary"
          >
            Get Started Free
          </button>
          <button
            onClick={navigateToTour}
            className="px-10 py-5 border-2 border-forest-900 text-forest-900 rounded-xl text-xl font-semibold hover:bg-forest-50 transition-all"
            data-testid="landing-final-cta-secondary"
          >
            Explore Features
          </button>
        </div>
      </div>
    </section>
  );
}

// ==================== 404 NOT FOUND PAGE ====================
function NotFoundPage() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-forest-50 to-emerald-50 dark:from-gray-900 dark:to-gray-800 p-4">
      <div className="text-center max-w-md" data-testid="not-found-page">
        <div className="flex justify-center mb-6">
          <div className="p-4 bg-forest-100 dark:bg-brand-navy/30 rounded-2xl">
            <AlertCircle className="h-16 w-16 text-forest-600 dark:text-forest-400" />
          </div>
        </div>
        <h1 className="text-6xl font-dm-sans font-bold text-forest-900 dark:text-white mb-4">404</h1>
        <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">Page not found. The page you're looking for doesn't exist or has been moved.</p>
        <Link
          to="/"
          data-testid="not-found-home-link"
          className="inline-flex items-center gap-2 px-8 py-3 bg-brand-navy text-white rounded-xl font-semibold hover:bg-brand-navy-dark transition-all"
        >
          <Home className="h-5 w-5" />
          Back to Home
        </Link>
      </div>
    </div>
  );
}

// ==================== FEATURES PAGE (standalone) ====================
function FeaturesPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900" data-testid="features-page">
      <header className="bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2">
            <TrendingUp className="h-8 w-8 text-forest-600" />
            <span className="text-xl font-dm-sans font-bold text-forest-900 dark:text-white">PortfolioEngine</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/pricing" className="text-gray-600 hover:text-forest-900 dark:text-gray-400 dark:hover:text-white">Pricing</Link>
            <Link to="/login" className="px-6 py-2 bg-brand-navy text-white rounded-lg font-semibold hover:bg-brand-navy-dark transition-all">Get Started</Link>
          </div>
        </div>
      </header>

      <section className="bg-brand-navy py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-dm-sans font-bold text-white mb-4">Why choose Portfolio Engine?</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 hover:shadow-xl transition-all border border-gray-100">
              <div className="w-14 h-14 bg-forest-100 rounded-xl flex items-center justify-center text-forest-900 mb-4">
                <Activity className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold text-forest-900 mb-2">Know the economic cycle</h3>
              <p className="text-gray-600">Understand whether the market is growing or slowing down, and what that means for your investments.</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 hover:shadow-xl transition-all border border-gray-100">
              <div className="w-14 h-14 bg-forest-100 rounded-xl flex items-center justify-center text-forest-900 mb-4">
                <Target className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold text-forest-900 mb-2">Diversify with confidence</h3>
              <p className="text-gray-600">Use smart allocations to spread your money across sectors and asset classes without needing to be an expert.</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 hover:shadow-xl transition-all border border-gray-100">
              <div className="w-14 h-14 bg-forest-100 rounded-xl flex items-center justify-center text-forest-900 mb-4">
                <Clock className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold text-forest-900 mb-2">Test your strategy</h3>
              <p className="text-gray-600">Backtest your portfolio against past market cycles to see how it might perform over time.</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 hover:shadow-xl transition-all border border-gray-100">
              <div className="w-14 h-14 bg-forest-100 rounded-xl flex items-center justify-center text-forest-900 mb-4">
                <BookOpen className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold text-forest-900 mb-2">Learn as you go</h3>
              <p className="text-gray-600">Explore our educational hub to grasp the basics of investing, risk management and different asset types.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 text-center">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-dm-sans font-bold text-forest-900 dark:text-white mb-6">Ready to get started?</h2>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-10 py-4 bg-brand-navy text-white rounded-xl text-lg font-semibold hover:bg-brand-navy-dark transition-all shadow-lg"
          >
            Get Started Free
            <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}

// ==================== TOOL REDIRECT (for shortcut URLs without portfolio context) ====================
function ToolRedirect() {
  const navigate = useNavigate();

  useEffect(() => {
    toast.info('Please select a portfolio first to access this tool.');
    navigate('/dashboard', { replace: true });
  }, [navigate]);

  return null;
}

// ==================== LOGIN PAGE ====================
function LoginPage() {
  const navigate = useNavigate();
  const authContext = React.useContext(AuthContext);
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'register'
  
  // Redirect if already authenticated (but only after context is loaded)
  useEffect(() => {
    if (authContext && !authContext.isLoading && authContext.isAuthenticated) {
      navigate('/dashboard');
    }
  }, [authContext, navigate]);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleAuthSuccess = (userData) => {
    if (authContext?.login) {
      authContext.login(userData);
    }
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-forest-50 to-emerald-50 dark:from-gray-900 dark:to-gray-800 p-4">
      <div className="max-w-md w-full space-y-6 p-8 bg-white dark:bg-gray-800 rounded-2xl shadow-xl">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-forest-100 dark:bg-brand-navy/30 rounded-xl">
              <TrendingUp className="h-10 w-10 text-forest-600 dark:text-forest-400" />
            </div>
          </div>
          <h2 className="text-3xl font-dm-sans font-bold text-forest-900 dark:text-white">
            {authMode === 'login' ? 'Welcome Back' : 'Create Account'}
          </h2>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            {authMode === 'login' ? 'Sign in to manage your investments' : 'Start your investment journey'}
          </p>
        </div>

        {/* Google Sign In */}
        <button
          data-testid="login-google-btn"
          onClick={handleGoogleLogin}
          className="w-full py-3 px-4 bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-all flex items-center justify-center gap-3 font-medium"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200 dark:border-gray-600"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">or continue with email</span>
          </div>
        </div>

        {/* Email/Password Form */}
        <AuthForm 
          mode={authMode}
          onSuccess={handleAuthSuccess}
          onSwitchMode={(mode) => setAuthMode(mode)}
        />
      </div>
    </div>
  );
}

function App() {
  const location = useLocation();

  // Check for session_id in URL hash synchronously
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Suspense fallback for lazy-loaded routes
  const LazyFallback = (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-forest-900 dark:border-forest-400 mx-auto"></div>
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading...</p>
      </div>
    </div>
  );

  return (
    <>
      <Toaster position="top-right" richColors />
      <React.Suspense fallback={LazyFallback}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
          <Route path="/market-analysis" element={<MarketAnalysis />} />
          <Route path="/market-analysis/advanced" element={<ProtectedRoute><AdvancedCycleAnalysis /></ProtectedRoute>} />
          <Route path="/portfolio/builder" element={<ProtectedRoute><PortfolioBuilder /></ProtectedRoute>} />
          <Route path="/portfolio/list" element={<ProtectedRoute><PortfolioList /></ProtectedRoute>} />
          <Route path="/portfolios" element={<ProtectedRoute><PortfolioList /></ProtectedRoute>} />
          <Route path="/portfolio/:portfolioId" element={<ProtectedRoute><PortfolioDetail /></ProtectedRoute>} />
          <Route path="/portfolio/:portfolioId/edit" element={<ProtectedRoute><EditPortfolio /></ProtectedRoute>} />
          <Route path="/portfolio/:portfolioId/delete" element={<ProtectedRoute><DeletePortfolio /></ProtectedRoute>} />
          <Route path="/portfolio/rebalancing" element={<ProtectedRoute><PortfolioRebalancing /></ProtectedRoute>} />
          <Route path="/portfolio/:portfolioId/rebalance" element={<ProtectedRoute><PortfolioRebalancing /></ProtectedRoute>} />
          <Route path="/portfolio/:portfolioId/health" element={<ProtectedRoute><PortfolioHealthPage /></ProtectedRoute>} />
          <Route path="/portfolio/compare" element={<ProtectedRoute><PortfolioComparison /></ProtectedRoute>} />
          <Route path="/instruments/compare" element={<ProtectedRoute><InstrumentComparison /></ProtectedRoute>} />
          <Route path="/instruments/evaluate" element={<ProtectedRoute><InstrumentEvaluator /></ProtectedRoute>} />
          <Route path="/ai-portfolio-builder" element={<ProtectedRoute><AIPortfolioBuilder /></ProtectedRoute>} />
          <Route path="/education" element={<ProtectedRoute><Education /></ProtectedRoute>} />
          <Route path="/education/metrics" element={<ProtectedRoute><EducationAdvanced /></ProtectedRoute>} />
          <Route path="/education/rebalancing" element={<ProtectedRoute><EducationRebalancing /></ProtectedRoute>} />
          <Route path="/education/:articleSlug" element={<ProtectedRoute><Education /></ProtectedRoute>} />
          <Route path="/brokers" element={<ProtectedRoute><Brokers /></ProtectedRoute>} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/guidance" element={<ProtectedRoute><ActionGuidance /></ProtectedRoute>} />
          <Route path="/backtesting" element={<ProtectedRoute><BacktestComparator /></ProtectedRoute>} />
          <Route path="/backtesting/:portfolioId" element={<ProtectedRoute><BacktestComparator /></ProtectedRoute>} />
          <Route path="/simulation" element={<ProtectedRoute><Backtesting /></ProtectedRoute>} />
          <Route path="/simulation/:portfolioId" element={<ProtectedRoute><Backtesting /></ProtectedRoute>} />
          <Route path="/alerts" element={<ProtectedRoute><AlertsCenter /></ProtectedRoute>} />
          <Route path="/activity" element={<ProtectedRoute><ActivityPage /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/learn" element={<LearnPage />} />
          <Route path="/market" element={<Navigate to="/market-analysis" replace />} />
          <Route path="/register" element={<Navigate to="/login" replace />} />
          <Route path="/signup" element={<Navigate to="/login" replace />} />
          <Route path="/rebalancing" element={<ProtectedRoute><ToolRedirect /></ProtectedRoute>} />
          <Route path="/instrument-evaluator" element={<ProtectedRoute><ToolRedirect /></ProtectedRoute>} />
          <Route path="/evaluator" element={<ProtectedRoute><ToolRedirect /></ProtectedRoute>} />
          <Route path="/tools" element={<ProtectedRoute><ToolRedirect /></ProtectedRoute>} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </React.Suspense>
      <CookieConsent />
    </>
  );
}

function AppWrapper() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <CyclePhaseProvider>
              <App />
            </CyclePhaseProvider>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

// ==================== LEGACY DASHBOARD (kept for reference) ====================
function DashboardLegacy({ user }) {
  const navigate = useNavigate();
  const [portfolioSummary, setPortfolioSummary] = useState({
    totalValue: 0,
    totalPnL: 0,
    totalPnLPercent: 0,
    portfolioCount: 0,
    distribution: []
  });
  const [loadingSummary, setLoadingSummary] = useState(true);

  useEffect(() => {
    if (user && !user.onboarding_completed) {
      navigate('/onboarding');
    }
  }, [user, navigate]);

  // Fetch portfolio summary
  useEffect(() => {
    const fetchPortfolioSummary = async () => {
      try {
        const response = await axios.get(`${API}/portfolio/list`, { withCredentials: true });
        const portfolios = response.data || [];
        
        if (portfolios.length === 0) {
          setPortfolioSummary({
            totalValue: 0,
            totalPnL: 0,
            totalPnLPercent: 0,
            portfolioCount: 0,
            distribution: []
          });
          setLoadingSummary(false);
          return;
        }

        // Fetch live data for all portfolios
        const liveDataPromises = portfolios.map(async (portfolio) => {
          try {
            const liveResponse = await axios.get(`${API}/portfolio/${portfolio.portfolio_id}/live`, { withCredentials: true });
            return liveResponse.data;
          } catch (err) {
            return portfolio;
          }
        });

        const livePortfolios = await Promise.all(liveDataPromises);
        
        // Calculate totals
        let totalCurrentValue = 0;
        let totalOriginalValue = 0;
        const instrumentTotals = {};

        livePortfolios.forEach(portfolio => {
          const currentValue = portfolio.total_current_value || portfolio.total_amount || 0;
          const originalValue = portfolio.total_original_value || portfolio.original_total_amount || portfolio.total_amount || 0;
          
          totalCurrentValue += currentValue;
          totalOriginalValue += originalValue;

          // Aggregate instruments by type
          (portfolio.instruments || []).forEach(inst => {
            const type = inst.type || 'other';
            const value = inst.current_value || (inst.current_price || inst.price || 0) * (inst.quantity || inst.units || 0);
            
            if (!instrumentTotals[type]) {
              instrumentTotals[type] = 0;
            }
            instrumentTotals[type] += value;
          });
        });

        // Calculate P&L
        const totalPnL = totalCurrentValue - totalOriginalValue;
        const totalPnLPercent = totalOriginalValue > 0 
          ? ((totalCurrentValue - totalOriginalValue) / totalOriginalValue) * 100 
          : 0;

        // Create distribution for pie chart
        const distribution = Object.entries(instrumentTotals)
          .filter(([_, value]) => value > 0)
          .map(([type, value]) => ({
            name: type === 'stocks' ? 'Stocks' 
              : type === 'etfs' ? 'ETFs' 
              : type === 'bonds' ? 'Bonds'
              : type === 'reits' ? 'REITs'
              : type === 'crypto' ? 'Crypto'
              : type === 'commodities' ? 'Commodities'
              : type === 'cash' ? 'Cash'
              : type.charAt(0).toUpperCase() + type.slice(1),
            value: Math.round(value * 100) / 100,
            percentage: totalCurrentValue > 0 ? (value / totalCurrentValue) * 100 : 0
          }))
          .sort((a, b) => b.value - a.value);

        setPortfolioSummary({
          totalValue: totalCurrentValue,
          totalPnL: Math.abs(totalPnLPercent) < 1000 ? totalPnL : 0, // Validate P&L
          totalPnLPercent: Math.abs(totalPnLPercent) < 1000 ? totalPnLPercent : 0,
          portfolioCount: portfolios.length,
          distribution
        });
      } catch (error) {
        console.error('Error fetching portfolio summary:', error);
      } finally {
        setLoadingSummary(false);
      }
    };

    fetchPortfolioSummary();
  }, []);

  // Colors for pie chart
  const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#6366F1', '#14B8A6'];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Use Navigation component */}
      <Navigation user={user} />

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="space-y-8">
          <div>
            <h1 className="text-4xl font-dm-sans font-bold text-forest-900">Welcome back, {getFirstName(user)}!</h1>
            <p className="text-gray-600 mt-2">Your investment dashboard</p>
          </div>

          {/* Profile Card + Portfolio Summary Row */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Profile Card */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-100">
              <h2 className="text-xl font-dm-sans font-bold mb-4">Your Profile</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Risk Profile</p>
                  {(() => {
                    const score = user?.risk_score;
                    const band = user?.risk_profile && user.risk_profile !== 'unknown'
                      ? user.risk_profile
                      : (typeof score === 'number' ? getRiskBand(score) : null);
                    if (!band) {
                      return <p className="text-lg font-semibold text-forest-900">Not set</p>;
                    }
                    const position = typeof score === 'number' ? getRiskBandPosition(score) : null;
                    const tooltip = typeof score === 'number' ? getRiskTooltip(score) : '';
                    return (
                      <div title={tooltip} data-testid="dashboard-risk-display">
                        <p className="text-lg font-semibold text-forest-900">{band}</p>
                        {position && (
                          <p className="text-xs text-gray-500 mt-0.5" data-testid="dashboard-risk-position">
                            {band} · {position}
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <div>
                  <p className="text-sm text-gray-600">Market Location</p>
                  <p className="text-lg font-semibold text-forest-900">{user?.location || 'Not set'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Subscription</p>
                  <p className="text-lg font-semibold text-forest-900 capitalize">{user?.subscription || 'Free'}</p>
                </div>
              </div>
            </div>

            {/* Portfolio Summary Card */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-100">
              <h2 className="text-xl font-dm-sans font-bold mb-4">Portfolio Summary</h2>
              {loadingSummary ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-forest-900"></div>
                </div>
              ) : portfolioSummary.portfolioCount === 0 ? (
                <div className="text-center py-8">
                  <Wallet className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No portfolios yet</p>
                  <button
                    onClick={() => navigate('/portfolio/builder')}
                    className="mt-3 px-4 py-2 bg-brand-navy text-white rounded-lg text-sm hover:bg-brand-navy-dark"
                  >
                    Create Your First Portfolio
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase">Total Value</p>
                    <p className="text-xl font-bold text-forest-900">
                      ${formatNumber(portfolioSummary.totalValue, 0)}
                    </p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase">P&L</p>
                    <p className={`text-xl font-bold ${portfolioSummary.totalPnL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {portfolioSummary.totalPnL >= 0 ? '+' : ''}
                      ${formatNumber(portfolioSummary.totalPnL, 0)}
                      <span className="text-sm ml-1">
                        ({portfolioSummary.totalPnLPercent >= 0 ? '+' : ''}{formatNumber(portfolioSummary.totalPnLPercent, 1)}%)
                      </span>
                    </p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase">Portfolios</p>
                    <p className="text-xl font-bold text-forest-900">{portfolioSummary.portfolioCount}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Holdings Distribution Pie Chart */}
          {portfolioSummary.distribution.length > 0 && (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-100">
              <h2 className="text-xl font-dm-sans font-bold mb-4">Holdings Distribution</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {/* Pie Chart */}
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={portfolioSummary.distribution}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percentage }) => `${name} ${formatNumber(percentage, 1)}%`}
                      >
                        {portfolioSummary.distribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value) => `$${formatNumber(value, 2)}`}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                {/* Legend with values */}
                <div className="space-y-2">
                  {portfolioSummary.distribution.map((item, index) => (
                    <div key={item.name} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div className="flex items-center">
                        <div 
                          className="w-4 h-4 rounded mr-3" 
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="font-medium">{item.name}</span>
                      </div>
                      <div className="text-right">
                        <span className="font-semibold">${formatNumber(item.value, 0)}</span>
                        <span className="text-gray-500 text-sm ml-2">({formatNumber(item.percentage, 1)}%)</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="grid md:grid-cols-4 gap-6">
            <button
              onClick={() => navigate('/market-analysis')}
              data-testid="dashboard-market-analysis-btn"
              className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 hover:shadow-lg transition-all hover:translate-y-[-2px] text-left"
            >
              <BarChart3 className="h-8 w-8 text-forest-900 mb-3" />
              <h3 className="text-lg font-dm-sans font-bold mb-1">Market Analysis</h3>
              <p className="text-sm text-gray-600">Analyze market phases and get AI insights</p>
            </button>

            <button
              onClick={() => navigate('/portfolio/builder')}
              data-testid="dashboard-build-portfolio-btn"
              className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 hover:shadow-lg transition-all hover:translate-y-[-2px] text-left"
            >
              <Wallet className="h-8 w-8 text-forest-900 mb-3" />
              <h3 className="text-lg font-dm-sans font-bold mb-1">Build Portfolio</h3>
              <p className="text-sm text-gray-600">Create a new optimized portfolio</p>
            </button>

            <button
              onClick={() => navigate('/instruments/evaluate')}
              data-testid="dashboard-evaluate-btn"
              className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 hover:shadow-lg transition-all hover:translate-y-[-2px] text-left"
            >
              <TrendingUp className="h-8 w-8 text-forest-900 mb-3" />
              <h3 className="text-lg font-dm-sans font-bold mb-1">Evaluate Instrument</h3>
              <p className="text-sm text-gray-600">Analyze stocks, ETFs, crypto & more</p>
            </button>

            <button
              onClick={() => navigate('/education')}
              data-testid="dashboard-education-btn"
              className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 hover:shadow-lg transition-all hover:translate-y-[-2px] text-left"
            >
              <BookOpen className="h-8 w-8 text-forest-900 mb-3" />
              <h3 className="text-lg font-dm-sans font-bold mb-1">Learn More</h3>
              <p className="text-sm text-gray-600">Explore investment education</p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Placeholder components for other routes
function Onboarding({ user }) {
  const navigate = useNavigate();
  const { setUser } = React.useContext(AuthContext);
  const [step, setStep] = useState(0); // Start at welcome step
  const [phone, setPhone] = useState('');
  const [location, setLocation] = useState('');
  const [responses, setResponses] = useState({});
  const [questions, setQuestions] = useState([]);
  const [riskProfile, setRiskProfile] = useState(null);

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/profile/risk-questions`);
      setQuestions(response.data);
    } catch (error) {
      console.error('Error fetching questions:', error);
    }
  };

  useEffect(() => {
    fetchQuestions();
  }, []);

  const handleNext = async () => {
    if (step === 0) {
      // Welcome step - just move to next
      setStep(1);
    } else if (step === 1) {
      // Location step
      try {
        await axios.put(`${API}/profile/update?phone=${phone}&location=${location}`, {}, { withCredentials: true });
        setStep(2);
      } catch (error) {
        toast.error('Failed to update profile');
      }
    } else if (step === 2) {
      // Risk assessment
      try {
        const formattedResponses = Object.entries(responses).map(([id, answer]) => ({
          question_id: parseInt(id),
          answer: answer
        }));
        
        const response = await axios.post(`${API}/profile/risk-assessment`, 
          { responses: formattedResponses },
          { withCredentials: true }
        );
        
        setRiskProfile(response.data);
        // Mark onboarding as completed in client state
        if (user) {
          setUser({ ...user, onboarding_completed: true, risk_profile: response.data.profile, risk_score: response.data.score });
        }
        toast.success(`Your risk profile: ${response.data.profile}`);
        setStep(3); // Move to choice step
      } catch (error) {
        toast.error('Failed to submit assessment');
      }
    }
  };

  const handleChoice = (choice) => {
    if (choice === 'existing') {
      navigate('/portfolio/builder');
    } else {
      navigate('/market-analysis', { state: { guidedFlow: true } });
    }
  };

  // Step indicators
  const steps = [
    { num: 1, label: 'Welcome' },
    { num: 2, label: 'Location' },
    { num: 3, label: 'Risk Profile' },
    { num: 4, label: 'Next Steps' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-forest-50 to-white py-12">
      <div className="max-w-3xl mx-auto px-4">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            {steps.map((s, idx) => (
              <div key={s.num} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  step >= idx ? 'bg-brand-navy text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  {s.num}
                </div>
                {idx < steps.length - 1 && (
                  <div className={`w-16 sm:w-24 h-1 mx-2 ${step > idx ? 'bg-brand-navy' : 'bg-gray-200'}`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2">
            {steps.map((s) => (
              <span key={s.num} className="text-xs text-gray-500">{s.label}</span>
            ))}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
          {/* Step 0: Welcome */}
          {step === 0 && (
            <div className="text-center space-y-6">
              <div className="w-20 h-20 bg-forest-100 rounded-full flex items-center justify-center mx-auto">
                <TrendingUp className="h-10 w-10 text-forest-900" />
              </div>
              <h1 className="text-3xl font-dm-sans font-bold text-forest-900">Welcome to PortfolioEngine!</h1>
              <p className="text-lg text-gray-600 max-w-xl mx-auto">
                Before you start building your investment portfolio, we need to understand your investor profile. 
                This will help us provide personalized recommendations tailored to your goals and risk tolerance.
              </p>
              <div className="bg-forest-50 p-6 rounded-lg text-left">
                <h3 className="font-bold text-forest-900 mb-3">What we will do:</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start space-x-2">
                    <span className="text-forest-500 mt-1">✓</span>
                    <span>Set up your market location for relevant data</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-forest-500 mt-1">✓</span>
                    <span>Complete a quick risk assessment questionnaire</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-forest-500 mt-1">✓</span>
                    <span>Determine your investor profile (Conservative, Moderate, or Aggressive)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-forest-500 mt-1">✓</span>
                    <span>Get personalized portfolio recommendations</span>
                  </li>
                </ul>
              </div>
              <p className="text-sm text-gray-500">
                <Shield className="inline h-4 w-4 mr-1" />
                This information is used only to personalize your experience.
              </p>
              <button
                onClick={handleNext}
                className="w-full py-4 bg-brand-navy text-white rounded-lg hover:bg-forest-500 transition-all text-lg font-medium"
                data-testid="onboarding-start-btn"
              >
                Get Started
              </button>
            </div>
          )}

          {/* Step 1: Location Setup */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h1 className="text-2xl font-dm-sans font-bold text-forest-900">Set Your Market Location</h1>
                <p className="text-gray-600 mt-2">This helps us provide relevant market data and broker recommendations.</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Market Region *</label>
                <select
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-500 focus:border-transparent text-lg"
                  data-testid="onboarding-location-select"
                >
                  <option value="">Select your primary market</option>
                  <option value="Global">🌍 Global (Multi-region)</option>
                  <option value="USA">🇺🇸 United States</option>
                  <option value="EUROPE">🇪🇺 Europe</option>
                  <option value="ASIA">🌏 Asia Pacific</option>
                  <option value="LATAM">🌎 Latin America</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number (Optional)</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-500 focus:border-transparent"
                  placeholder="+1 234 567 8900"
                  data-testid="onboarding-phone-input"
                />
                <p className="text-xs text-gray-500 mt-1">For account recovery and important notifications</p>
              </div>

              <button
                onClick={handleNext}
                disabled={!location}
                className="w-full py-4 bg-brand-navy text-white rounded-lg hover:bg-forest-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-lg font-medium"
                data-testid="onboarding-next-btn"
              >
                Continue to Risk Assessment
              </button>
            </div>
          )}

          {/* Step 2: Risk Assessment */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h1 className="text-2xl font-dm-sans font-bold text-forest-900">Risk Profile Assessment</h1>
                <p className="text-gray-600 mt-2">
                  Answer these questions to help us understand your investment style and risk tolerance.
                </p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-amber-800">
                  <strong>Tip:</strong> Answer based on how you would actually react, not how you think you should react. 
                  There are no wrong answers!
                </p>
              </div>

              <div className="space-y-8">
                {questions.map((q, qIdx) => (
                  <div key={q.id} className="space-y-3 pb-6 border-b border-gray-100 last:border-0">
                    <p className="font-medium text-gray-900">
                      <span className="text-forest-500 mr-2">{qIdx + 1}.</span>
                      {q.question}
                    </p>
                    <div className="space-y-2 ml-6">
                      {q.options.map((option, idx) => (
                        <label key={idx} className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-all ${
                          responses[q.id] === idx + 1 
                            ? 'bg-forest-50 border-2 border-forest-500' 
                            : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                        }`}>
                          <input
                            type="radio"
                            name={`question_${q.id}`}
                            value={idx + 1}
                            checked={responses[q.id] === idx + 1}
                            onChange={() => setResponses({ ...responses, [q.id]: idx + 1 })}
                            className="text-forest-900 focus:ring-forest-500 h-4 w-4"
                            data-testid={`risk-question-${q.id}-option-${idx + 1}`}
                          />
                          <span className="text-gray-700">{stripOptionScore(option)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between pt-4">
                <span className="text-sm text-gray-500">
                  {Object.keys(responses).length} of {questions.length} answered
                </span>
                <button
                  onClick={handleNext}
                  disabled={Object.keys(responses).length < questions.length}
                  className="px-8 py-3 bg-brand-navy text-white rounded-lg hover:bg-forest-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
                  data-testid="onboarding-submit-btn"
                >
                  See My Profile
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Results & Choice */}
          {step === 3 && riskProfile && (() => {
            const band = getRiskBand(riskProfile.score);
            const color = getRiskBandColor(band);
            const pct = getRiskPercent(riskProfile.score);
            const description = getRiskBandDescription(band);
            const position = getRiskBandPosition(riskProfile.score);
            const tooltip = getRiskTooltip(riskProfile.score);
            return (
            <div className="space-y-8">
              <div className="max-w-md mx-auto text-center" data-testid="onboarding-risk-result">
                <p
                  className="font-medium text-gray-900 dark:text-white cursor-help"
                  style={{ fontSize: '24px', fontWeight: 500 }}
                  title={tooltip}
                  data-testid="onboarding-risk-band"
                >
                  {band}
                </p>
                <div className="mt-4 w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" aria-hidden="true">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${pct}%`, backgroundColor: color }}
                    data-testid="onboarding-risk-bar"
                  />
                </div>
                {position && (
                  <p
                    className="mt-2 text-gray-500 dark:text-gray-400"
                    style={{ fontSize: '13px' }}
                    title={tooltip}
                    data-testid="onboarding-risk-position"
                  >
                    {band} · {position}
                  </p>
                )}
                <p className="mt-3 text-gray-700 dark:text-gray-300 text-sm" data-testid="onboarding-risk-description">
                  {description}
                </p>
              </div>

              <div className="border-t pt-8">
                <h2 className="text-xl font-dm-sans font-bold text-center mb-6">What would you like to do next?</h2>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <button
                    onClick={() => handleChoice('existing')}
                    className="p-6 bg-white border-2 border-gray-200 rounded-lg hover:border-forest-500 hover:shadow-lg transition-all text-left group"
                    data-testid="choice-existing-portfolio"
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-12 h-12 bg-forest-100 rounded-lg flex items-center justify-center group-hover:bg-forest-200 transition-all">
                        <Wallet className="h-6 w-6 text-forest-900" />
                      </div>
                      <h3 className="text-lg font-bold">I have a portfolio</h3>
                    </div>
                    <p className="text-gray-600 text-sm">
                      I already know what I want to invest in. Take me directly to create my portfolio.
                    </p>
                  </button>

                  <button
                    onClick={() => handleChoice('guided')}
                    className="p-6 bg-brand-navy text-white rounded-lg hover:bg-brand-navy-dark hover:shadow-lg transition-all text-left group"
                    data-testid="choice-guided-flow"
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-12 h-12 bg-brand-navy rounded-lg flex items-center justify-center">
                        <BarChart3 className="h-6 w-6 text-white" />
                      </div>
                      <h3 className="text-lg font-bold">Guide me through</h3>
                    </div>
                    <p className="text-forest-100 text-sm">
                      Analyze the market first, understand the current cycle, and get instrument recommendations.
                    </p>
                    <span className="inline-block mt-3 text-xs bg-brand-navy px-2 py-1 rounded">Recommended</span>
                  </button>
                </div>
              </div>
            </div>
            );
          })()}
        </div>

        {/* Disclaimer */}
        <p className="text-center text-xs text-gray-500 mt-6">
          ⚠️ This app is for educational purposes only. Always consult with a financial advisor before making investment decisions.
        </p>
      </div>
    </div>
  );
}

// Main export
export default AppWrapper;